export type TickCallback = {
  onTick: (tickTime: number) => void;
  onVisibilityResume?: (elapsedMs: number) => void;
};

export default class PollingClock {
  static readonly TICK_INTERVAL_MS = 1000;

  private subscribers: Set<TickCallback> = new Set();
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private pausedAt: number | null = null;

  subscribe(callback: TickCallback): () => void {
    const wasEmpty = this.subscribers.size === 0;
    this.subscribers.add(callback);

    if (wasEmpty) {
      this.start();
    }

    return () => {
      this.subscribers.delete(callback);
      if (this.subscribers.size === 0) {
        this.stop();
      }
    };
  }

  destroy(): void {
    this.stop();
    this.subscribers.clear();
  }

  private start(): void {
    if (this.intervalId !== null) {
      return;
    }

    this.intervalId = setInterval(() => {
      this.tick();
    }, PollingClock.TICK_INTERVAL_MS);

    if (typeof document !== 'undefined') {
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
      );
    }
  }

  private stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (typeof document !== 'undefined') {
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
      );
    }

    this.pausedAt = null;
  }

  private tick(): void {
    if (this.pausedAt !== null) {
      return;
    }

    const tickTime = Date.now();
    this.subscribers.forEach((callback) => {
      callback.onTick(tickTime);
    });
  }

  private handleVisibilityChange = (): void => {
    if (typeof document === 'undefined') {
      return;
    }

    if (document.visibilityState === 'hidden') {
      this.pausedAt = Date.now();
    } else if (
      document.visibilityState === 'visible' &&
      this.pausedAt !== null
    ) {
      const elapsedMs = Date.now() - this.pausedAt;
      this.pausedAt = null;

      this.subscribers.forEach((callback) => {
        if (callback.onVisibilityResume) {
          callback.onVisibilityResume(elapsedMs);
        }
      });
    }
  };
}
